<script lang="ts">
  import { tasks, det, monteCarloResult, risks, wbsNodes, iterations, runMonteCarloNow, openEditModal, projectPhases, twoShifts } from "../lib/stores";
  import { wbsLabel } from "../lib/wbs";
  import { phaseColor } from "../lib/phases";
  import { fmt1 } from "../lib/format";
  import { risksLinkedInText } from "../lib/risk";
  import { taskShiftsPerDay } from "../lib/cpm";
  import type { RiskEntry, Task } from "../lib/types";

  interface WatchRow {
    task: Task;
    ci: number; // 0-100
    tier: "high" | "medium";
    reasonText: string;
  }

  let running = false;
  function run() {
    running = true;
    runMonteCarloNow();
    running = false;
  }

  $: result = $det;
  $: mc = $monteCarloResult;

  function floatLabel(days: number): string {
    const d = Math.round(days);
    return d <= 0 ? "under a day" : `${d} day${d === 1 ? "" : "s"}`;
  }

  function buildReason(t: Task, floatDays: number, linked: RiskEntry[], projectShiftsPerDay: 1 | 2): string {
    const parts = [`${floatLabel(floatDays)} of float today`];
    const shiftsPerDay = taskShiftsPerDay(t, projectShiftsPerDay);
    const o = t.o / shiftsPerDay, m = t.m / shiftsPerDay, p = t.p / shiftsPerDay;
    if (p - o >= 4) parts.push(`wide duration estimate (${fmt1(o)}–${fmt1(m)}–${fmt1(p)}d)`);
    if (linked.length) parts.push(`linked to ${linked.map((r) => r.id).join(", ")} in the risk register`);
    return parts.join(" · ");
  }

  $: watchlist = ((): WatchRow[] => {
    if (!result.ok || !mc) return [];
    const rows: WatchRow[] = [];
    for (const t of $tasks) {
      if (t.type !== "activity") continue;
      if (result.critical[t.id] || result.complete[t.id]) continue;
      const ciFrac = mc.criticalityIndex[t.id] ?? 0;
      if (ciFrac < 0.2) continue;
      const floatDays = result.LS[t.id] - result.ES[t.id];
      const linked = risksLinkedInText(t.risk, $risks);
      rows.push({
        task: t,
        ci: Math.round(ciFrac * 100),
        tier: ciFrac >= 0.5 ? "high" : "medium",
        reasonText: buildReason(t, floatDays, linked, $twoShifts ? 2 : 1),
      });
    }
    return rows.sort((a, b) => b.ci - a.ci);
  })();

  $: highCount = watchlist.filter((r) => r.tier === "high").length;
</script>

<div class="tab-panel group">
  <div class="group-head">
    <span class="group-tag briefing">Briefing</span>
    <span class="group-desc">
      A plain-language read of the schedule for anyone who doesn't need the Gantt — what's driving
      the finish date today, and what could still move it.
    </span>
  </div>

  {#if !result.ok}
    <p class="summary-empty">Fix the schedule error above to see this briefing.</p>
  {:else if !mc}
    <section class="summary-block">
      <h3 class="summary-head">Run the schedule-risk simulation</h3>
      <p class="summary-empty">
        This briefing needs a Monte Carlo run to know which activities carry real schedule risk beyond
        the deterministic critical path — {$iterations.toLocaleString()} iterations, same setting as the
        Analysis tab.
      </p>
      <button class="btn primary" type="button" disabled={running} on:click={run}>Run simulation</button>
    </section>
  {:else}
    <div class="summary">
      <div class="kpis">
        <div class="kpi">
          <div class="kpi-label">Fastest-case plan</div>
          <div class="kpi-value">{fmt1(result.projectEnd)} <span>days</span></div>
          <div class="kpi-note">If every activity lands on its most likely duration</div>
        </div>
        <div class="kpi" class:bad={mc.p50 - result.projectEnd > 0.5}>
          <div class="kpi-label">Most likely outcome</div>
          <div class="kpi-value">{fmt1(mc.p50)} <span>days</span></div>
          <div class="kpi-note">Once normal schedule uncertainty is factored in</div>
        </div>
        <div class="kpi">
          <div class="kpi-label">Realistic range</div>
          <div class="kpi-value">{fmt1(mc.p10)}–{fmt1(mc.p90)} <span>days</span></div>
          <div class="kpi-note">80% of simulated outcomes fall in this band</div>
        </div>
        <div class="kpi" class:bad={watchlist.length > 0}>
          <div class="kpi-label">Elevated schedule risk</div>
          <div class="kpi-value">{watchlist.length} <span>activities</span></div>
          <div class="kpi-note">
            {#if watchlist.length}
              {highCount} high, {watchlist.length - highCount} medium &mdash; none flagged by the critical path alone
            {:else}
              Nothing outside the critical path shows meaningful risk
            {/if}
          </div>
        </div>
      </div>

      <p class="body-note">
        The fastest-case plan is what today's critical path shows — it's what happens if nothing slips.
        It doesn't flag activities that aren't on that path yet but have a real chance of ending up
        there once normal delivery and testing delays are factored in. That's what the list below is for.
      </p>

      <section class="summary-block">
        <h3 class="summary-head">
          Activities to watch
          <span class="summary-sub">ranked by how often each one ends up driving the finish date, across {mc.iterations.toLocaleString()} simulated runs</span>
        </h3>
        {#if !watchlist.length}
          <p class="summary-empty">No non-critical activity showed meaningful risk in this run.</p>
        {:else}
          <ol class="watch-list">
            {#each watchlist as row (row.task.id)}
              <li class="watch-row">
                <div class="watch-body">
                  <button class="watch-main" type="button" on:click={() => openEditModal(row.task.id)}>
                    <span class="chain-dot" style="background:{phaseColor($projectPhases, row.task.phase)}"></span>
                    <span class="mono">{wbsLabel(row.task, $wbsNodes)}</span>
                    <span class="chain-name">{row.task.name}</span>
                    <span class="watch-phase">{row.task.phase}</span>
                  </button>
                  <span class="watch-reason">{row.reasonText}</span>
                </div>
                <span class="watch-side">
                  <span class="risk-score" class:high={row.tier === "high"} class:medium={row.tier === "medium"}>{row.tier}</span>
                  <span class="watch-pct mono">{row.ci}%</span>
                </span>
              </li>
            {/each}
          </ol>
        {/if}
      </section>

      {#if watchlist.length}
        <section class="summary-block">
          <h3 class="summary-head">Suggested next steps</h3>
          <ul class="actions-list">
            {#each watchlist.slice(0, 3) as row (row.task.id)}
              <li>
                Review <button class="linkish" type="button" on:click={() => openEditModal(row.task.id)}>{row.task.name}</button>
                — {row.reasonText}.
              </li>
            {/each}
            <li>Re-run the simulation after the schedule next updates — this ranking shifts as activities complete or slip.</li>
          </ul>
        </section>
      {/if}

      <p class="summary-foot">
        Criticality index: the share of {mc.iterations.toLocaleString()} simulated schedules — each
        activity's duration drawn from its optimistic/likely/pessimistic estimate — in which an activity
        ended up with zero slack. Activities already on the deterministic critical path, already
        complete, or below 20% are left off this list. Float, duration range and risk-register links
        come from the current plan.
      </p>
    </div>
  {/if}
</div>
